import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getOpportunities } from "../services/opportunityService.js";

const statusColor = (status) => {
  if (status === "qualified") return "success";
  if (status === "draft") return "default";
  return "info";
};

const OpportunitiesView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState([]);
  const [error, setError] = useState(null);

  const loadOpportunities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getOpportunities();
      if (response.success) {
        setOpportunities(response.opportunities || []);
      } else {
        setError(response.error || "Failed to load opportunities");
      }
    } catch (err) {
      setError(err.message || "Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Opportunities</Typography>
        <Button variant="outlined" onClick={loadOpportunities}>
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Move date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {opportunities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">
                      No opportunities yet. Convert a lead to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {opportunities.map((opp) => (
                <TableRow key={opp.id} hover>
                  <TableCell>{opp.opportunityNumber}</TableCell>
                  <TableCell>{opp.customer?.name || "—"}</TableCell>
                  <TableCell>
                    {opp.move?.movingFrom || "—"} → {opp.move?.movingTo || "—"}
                  </TableCell>
                  <TableCell>
                    {opp.move?.earliestMoveDate ||
                      opp.move?.moveDates?.[0] ||
                      "TBC"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={opp.status}
                      color={statusColor(opp.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => navigate(`/opportunities/${opp.id}`)}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OpportunitiesView;
